import { useState } from "react";
import classNames from "classnames";
import format from "string-template";
import {
  MdExpandLess,
  MdExpandMore,
  MdHearing,
  MdLocalHospital,
  MdMedicalServices,
  MdMood,
  MdSpa,
  MdVisibility,
  MdVolunteerActivism,
} from "react-icons/md";
import { isBoostedLimit } from "utils/helpers/coverageLimits";
import {
  columnValue,
  coverageSections,
  groupedBenefits,
} from "../coverageGrid";

const EMPTY_VALUES = ["-", "_"];

const sectionIcon = (name) => {
  if (/HOSPITALISATION/i.test(name)) return MdLocalHospital;
  if (/SOINS COURANTS/i.test(name)) return MdMedicalServices;
  if (/BIEN-ETRE|BIEN-ÊTRE/i.test(name)) return MdSpa;
  if (/OPTIQUE/i.test(name)) return MdVisibility;
  if (/AUDITIVES/i.test(name)) return MdHearing;
  if (/DENTAIRE/i.test(name)) return MdMood;
  return MdVolunteerActivism;
};

const ValueCell = ({ benefit, column, emptyLabel }) => {
  const value = columnValue(column, benefit.id);
  const isEmpty = !value || EMPTY_VALUES.includes(value);
  const boosted =
    !isEmpty &&
    isBoostedLimit(
      column.limits && column.limits[benefit.id],
      column.upsellIds,
    );
  return (
    <td
      className={classNames("ac-collectif__grid-value", {
        "ac-collectif__grid-value--empty": isEmpty,
        "ac-collectif__grid-value--boosted": boosted,
      })}
    >
      {isEmpty ? emptyLabel : value}
    </td>
  );
};

const GroupRows = ({ group, columns, showGhostColumn, emptyLabel }) => (
  <>
    {group.subSection && (
      <tr className="ac-collectif__grid-subsection">
        <td colSpan={columns.length + (showGhostColumn ? 2 : 1)}>
          {group.subSection}
        </td>
      </tr>
    )}
    {group.items.map((benefit) => (
      <tr key={benefit.id}>
        <td className="ac-collectif__grid-label">{benefit.type}</td>
        {columns.map((column) => (
          <ValueCell
            key={column.rank}
            benefit={benefit}
            column={column}
            emptyLabel={emptyLabel}
          />
        ))}
        {showGhostColumn && <td />}
      </tr>
    ))}
  </>
);

const SectionBlock = ({
  section,
  columns,
  showGhostColumn,
  translation: T,
}) => {
  const [open, setOpen] = useState(true);
  const Icon = sectionIcon(section.label);
  const groups = groupedBenefits(section.benefits);
  const ToggleIcon = open ? MdExpandLess : MdExpandMore;

  return (
    <div className="ac-collectif__grid-section">
      <button
        type="button"
        className="ac-collectif__grid-toggle"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
      >
        <span className="ac-collectif__grid-toggle-label">
          <Icon size={20} />
          <span>
            <strong>{section.label}</strong>
            <span className="ac-collectif__grid-toggle-count">
              {format(T.OFFER.GRID.POST_COUNT, {
                count: section.benefits.length,
              })}
            </span>
          </span>
        </span>
        <ToggleIcon size={18} />
      </button>
      {open && (
        <div className="ac-collectif__grid-body">
          <table className="ac-collectif__grid-table">
            <thead>
              <tr>
                <th className="ac-collectif__grid-label">
                  {T.OFFER.GRID.POST}
                </th>
                {columns.map((column) => (
                  <th key={column.rank}>{T.RANKS[column.rank]}</th>
                ))}
                {showGhostColumn && <th />}
              </tr>
            </thead>
            <tbody>
              {groups.map((group) => (
                <GroupRows
                  key={[group.subSection || "root", group.items[0].id].join(
                    "-",
                  )}
                  group={group}
                  columns={columns}
                  showGhostColumn={showGhostColumn}
                  emptyLabel={T.OFFER.GRID.EMPTY}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const GuaranteeGrid = ({ translation: T, plans, columns, showGhostColumn }) => (
  <div className="ac-collectif__grid">
    <div className="ac-collectif__grid-head">
      <h4 className="ac-collectif__grid-title">{T.OFFER.GRID.TITLE}</h4>
      <p className="ac-collectif__grid-description">
        {T.OFFER.GRID.DESCRIPTION}
      </p>
    </div>
    {coverageSections(plans).map((section) => (
      <SectionBlock
        key={section.id}
        section={section}
        columns={columns}
        showGhostColumn={showGhostColumn}
        translation={T}
      />
    ))}
  </div>
);

export default GuaranteeGrid;
